<template>
  <div class="min-h-screen flex flex-col">

    <!-- Navigation progress bar: gele lijn bovenaan die direct reageert op klik -->
    <div class="nav-progress-bar" :class="{ active: navActive, done: navDone }"></div>

    <!-- Leveringstijd-melding — sitewide topbalk -->
    <div v-if="!minimalHeader" class="bg-brand text-ink text-center text-[0.78rem] font-medium py-2 px-4 leading-snug">
      <i class="bi bi-lightning-charge-fill mr-1.5" aria-hidden="true"></i>
      Voor 15:00 besteld = morgen in huis &nbsp;·&nbsp; Gratis verzending vanaf €50 &nbsp;·&nbsp; 2 jaar garantie
    </div>

    <!-- Volledige navbar - verborgen op pagina's met eigen minimale header -->
    <Header v-if="!minimalHeader" />
    <AccountBanner v-if="!minimalHeader" />

    <!-- Main Content -->
    <main class="flex-1">
      <router-view v-slot="{ Component }">
        <!-- Geen mode="out-in": oud en nieuw overlappen kort → geen zwarte leegte -->
        <!-- transition ALLEEN actief na een echte gebruikersnavigatie (hasNavigated).
             Dit voorkomt dat page-fade-enter-from (opacity:0) vuurt bij:
             (a) de eerste SSR-hydration, of
             (b) een hydration-mismatch die Vue dwingt tot een volledige re-render.
             De rAF-callback van <transition> wordt namelijk ná onMounted geplanned,
             waardoor mounted=true al gezet is als de klasse wordt toegepast. -->
        <transition :name="hasNavigated ? 'page-fade' : ''">
          <component :is="Component" :key="$route.path" />
        </transition>
      </router-view>
    </main>

    <!-- Globale modals (teleport to body) -->
    <AddToCartModal />
    <DiscountPopup />

    <!-- Cookie-toestemming (AVG / Consent Mode v2) -->
    <CookieConsent />
    <CartToast />

    <!-- Footer - verborgen op pagina's met minimale header (account, checkout, thank-you) -->
    <footer v-if="!minimalHeader" ref="footerRef" class="bg-ink text-white footer-reveal" aria-label="Sitefooter">

      <!-- Gele accentlijn bovenaan -->
      <div class="h-1 w-full bg-gradient-to-r from-brand via-brand-hover to-brand"></div>

      <!-- Trust balk -->
      <div class="border-b border-white/10">
        <div class="max-w-screen-xl mx-auto px-6">
          <!-- Op mobiel 2×2 grid; op lg 4 in één rij.
               border-right op elke cel (behalve de laatste per rij) in plaats
               van divide-x zodat de scheidslijn op mobiel netjes klopt. -->
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-0">
            <div class="flex items-center gap-2.5 py-4 px-3 md:px-6 border-r border-b lg:border-b-0 border-white/10">
              <i class="bi bi-truck text-brand text-[1.1rem] md:text-[1.25rem] shrink-0" aria-hidden="true"></i>
              <span class="text-[0.75rem] md:text-[0.85rem] text-ink-7 leading-[1.3]">Gratis verzending<br><strong class="text-white font-semibold">vanaf €50</strong></span>
            </div>
            <div class="flex items-center gap-2.5 py-4 px-3 md:px-6 border-b lg:border-b-0 lg:border-r border-white/10">
              <i class="bi bi-shield-check text-brand text-[1.1rem] md:text-[1.25rem] shrink-0" aria-hidden="true"></i>
              <span class="text-[0.75rem] md:text-[0.85rem] text-ink-7 leading-[1.3]">2 jaar garantie<br><strong class="text-white font-semibold">standaard op alles</strong></span>
            </div>
            <div class="flex items-center gap-2.5 py-4 px-3 md:px-6 border-r border-white/10">
              <i class="bi bi-arrow-clockwise text-brand text-[1.1rem] md:text-[1.25rem] shrink-0" aria-hidden="true"></i>
              <span class="text-[0.75rem] md:text-[0.85rem] text-ink-7 leading-[1.3]">30 dagen retour<br><strong class="text-white font-semibold">niet goed, geld terug</strong></span>
            </div>
            <div class="flex items-center gap-2.5 py-4 px-3 md:px-6">
              <i class="bi bi-lock text-brand text-[1.1rem] md:text-[1.25rem] shrink-0" aria-hidden="true"></i>
              <span class="text-[0.75rem] md:text-[0.85rem] text-ink-7 leading-[1.3]">Veilig betalen<br><strong class="text-white font-semibold">via iDeal, Visa &amp; meer</strong></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Hoofd footer -->
      <div class="max-w-screen-xl mx-auto px-6 pt-14 pb-10">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[2.5fr_1fr_1fr_1fr] gap-10 lg:gap-16 mb-12">

          <!-- Kolom 1: Logo + tekst + socials -->
          <div>
            <div class="mb-5"><Logo :height="34" :inverted="true" /></div>
            <p class="text-[0.9rem] text-ink-5 leading-[1.75] mb-6 max-w-[280px]">
              Thuisbatterijen en energieopslag van A-merken.
              Eerlijk geprijsd, morgen in huis.
            </p>
            <div class="flex gap-3">
              <!-- Alleen socials met een echte URL worden een link; de rest verschijnt
                   niet (voorkomt dode '#'-links die het vertrouwen schaden). -->
              <a
                v-for="social in activeSocials"
                :key="social.label"
                :href="social.href"
                :aria-label="`Plug2Charge op ${social.label}`"
                target="_blank"
                rel="noopener"
                class="social-icon w-9 h-9 bg-white/8 rounded-lg flex items-center justify-center text-ink-5 text-[1rem] no-underline"
              >
                <i :class="`bi ${social.icon}`" aria-hidden="true"></i>
              </a>
            </div>
          </div>

          <!-- Kolom 2: Klantenservice -->
          <div>
            <h2 class="text-[0.75rem] font-bold tracking-[0.1em] uppercase text-brand mb-5">Klantenservice</h2>
            <ul class="list-none p-0 m-0 space-y-2">
              <li v-for="link in serviceLinks" :key="link.to">
                <router-link :to="link.to" class="inline-block text-[0.875rem] text-ink-5 no-underline transition-colors duration-200 hover:text-white">
                  {{ link.label }}
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Kolom 3: Informatie -->
          <div>
            <h2 class="text-[0.75rem] font-bold tracking-[0.1em] uppercase text-brand mb-5">Informatie</h2>
            <ul class="list-none p-0 m-0 space-y-2">
              <li v-for="link in infoLinks" :key="link.to">
                <router-link :to="link.to" class="inline-block text-[0.875rem] text-ink-5 no-underline transition-colors duration-200 hover:text-white">
                  {{ link.label }}
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Kolom 4: Mijn Account -->
          <div>
            <h2 class="text-[0.75rem] font-bold tracking-[0.1em] uppercase text-brand mb-5">Mijn Account</h2>
            <ul class="list-none p-0 m-0 space-y-2">
              <li v-for="link in accountLinks" :key="link.to">
                <router-link :to="link.to" class="inline-block text-[0.875rem] text-ink-5 no-underline transition-colors duration-200 hover:text-white">
                  {{ link.label }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>

        <!-- Onderbalk: betaalmethoden + copyright -->
        <div class="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-5">

          <!-- Betaalmethoden -->
          <div class="flex items-center gap-2 flex-wrap justify-center sm:justify-start">
            <span class="text-[0.775rem] text-ink-5 font-semibold uppercase tracking-[0.08em] mr-1">Betaal met:</span>
            <!-- Betaal-iconen zijn trust-badges, geen navigatie: geen dode links. -->
            <span
              v-for="pm in paymentMethods"
              :key="pm.key"
              :title="pm.label"
              class="inline-flex items-center justify-center rounded bg-white/8 p-[5px] select-none"
              :style="{ height: '28px', minWidth: pm.wide ? '52px' : '40px' }"
            >
              <img
                :src="`/img/payment/${pm.key}.svg`"
                :alt="pm.label"
                class="h-full w-auto object-contain"
                loading="lazy"
              />
            </span>
          </div>

          <!-- Copyright + bedrijfsgegevens -->
          <div class="text-[0.8rem] text-ink-5 text-center sm:text-right space-y-[0.25rem]">
            <p class="m-0 flex items-center justify-center sm:justify-end gap-1.5">
              <span>🇳🇱</span>
              <strong class="text-ink-7">Nederlands bedrijf</strong>
              <span class="text-ink-6">— veilig winkelen</span>
            </p>
            <p class="m-0">&copy; {{ currentYear }} <strong class="text-ink-7">Plug2charge.nl</strong> (Slimmemeterwinkel.nl)</p>
            <p class="m-0">Poortdijkstraat 13, 4318AL Brouwershaven</p>
            <p class="m-0">KVK 76253937 &nbsp;&bull;&nbsp; BTW NL003065325B15</p>
            <p class="m-0">
              Gebouwd door
              <a href="https://digiventures.nl" target="_blank" rel="noopener" class="text-brand no-underline hover:underline">Digiventures</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
const currentYear = new Date().getFullYear()
import { useRoute, useRouter } from 'vue-router'
import { useCartStore } from '@stores/cart'
import { useWishlistStore } from '@stores/wishlist'
import { useAuthStore } from '@stores/auth'
import Header from '@components/Header.vue'
import AccountBanner from '@components/AccountBanner.vue'
import Logo from '@components/Logo.vue'
import AddToCartModal from '@components/AddToCartModal.vue'
import DiscountPopup from '@components/DiscountPopup.vue'
import CookieConsent from '@components/CookieConsent.vue'
import CartToast from '@components/CartToast.vue'

const route         = useRoute()
const router        = useRouter()
const cartStore     = useCartStore()
const wishlistStore = useWishlistStore()
const authStore     = useAuthStore()

// hasNavigated wordt pas true na een echte gebruikersnavigatie (niet de initiële
// page-load navigatie). De initiële navigatie heeft from.name === null (START_LOCATION).
// Daarna pas zetten we de transition aan - zo vuurt page-fade nooit bij hydration.
const hasNavigated = ref(false)

// Routes die een eigen minimale header hebben
const MINIMAL_HEADER_ROUTES = ['/checkout', '/thank-you']
const minimalHeader = computed(() => {
  // Checkout & thank-you: altijd minimale header
  if (MINIMAL_HEADER_ROUTES.some(r => route.path.startsWith(r))) return true
  // Account-pagina: minimale header als de gebruiker NIET is ingelogd
  // (ingelogde gebruikers zien hun account-dashboard mét normale navbar)
  if (route.path.startsWith('/account') && !authStore.isLoggedIn) return true
  return false
})

// ── Navigation progress bar ──────────────────────────────────────────────────
// navActive: bar is zichtbaar en animeert naar 85%
// navDone:   bar springt naar 100% en fadeet uit
const navActive = ref(false)
const navDone   = ref(false)
let navTimer    = null

router.beforeEach(() => {
  navDone.value   = false
  navActive.value = true
  // Safety: als after-each nooit vurde (b.v. redirect), reset na 8s
  clearTimeout(navTimer)
  navTimer = setTimeout(() => { navDone.value = true; navActive.value = false }, 8000)
})

router.afterEach((to, from) => {
  clearTimeout(navTimer)
  navDone.value = true
  setTimeout(() => { navActive.value = false; navDone.value = false }, 400)
  // Activeer page-fade transition pas ná de eerste echte gebruikersnavigatie.
  // De initiële navigatie heeft from.name === null (Vue Router START_LOCATION).
  if (from.name !== null) hasNavigated.value = true
})

// Footer fade-in via IntersectionObserver
const footerRef = ref(null)
let footerObserver = null

onMounted(() => {
  cartStore.initCart()
  wishlistStore.initWishlist()
  authStore.init()

  if (!footerRef.value) return

  // Als de footer al in het viewport staat (korte pagina's zoals cart/checkout),
  // meteen tonen - geen observer nodig.
  const rect = footerRef.value.getBoundingClientRect()
  if (rect.top < window.innerHeight) {
    footerRef.value.classList.add('footer-visible')
    return
  }

  // Lange pagina's: animeer footer zodra hij in beeld scrollt
  footerObserver = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      entry.target.classList.add('footer-visible')
      footerObserver.disconnect()
    }
  }, { threshold: 0, rootMargin: '0px 0px 100px 0px' })

  footerObserver.observe(footerRef.value)
})

onUnmounted(() => { footerObserver?.disconnect() })

// Vul hier de echte profiel-URL's in; laat op '' staan wat (nog) niet bestaat.
// Alleen ingevulde profielen worden als link getoond (activeSocials hieronder).
const socials = [
  { label: 'Facebook',  icon: 'bi-facebook',  href: '' },
  { label: 'Instagram', icon: 'bi-instagram', href: '' },
  { label: 'LinkedIn',  icon: 'bi-linkedin',  href: '' },
  { label: 'YouTube',   icon: 'bi-youtube',   href: '' },
]
const activeSocials = computed(() =>
  socials.filter(s => s.href && s.href !== '#')
)

const serviceLinks = [
  { to: '/contact',    label: 'Contact' },
  { to: '/verzending', label: 'Verzending & Levering' },
  { to: '/retourneren',label: 'Retourneren' },
  { to: '/veelgestelde-vragen', label: 'Veelgestelde Vragen' },
]

const infoLinks = [
  { to: '/over-ons',              label: 'Over Ons' },
  { to: '/algemene-voorwaarden',  label: 'Algemene Voorwaarden' },
  { to: '/privacy',               label: 'Privacybeleid' },
  { to: '/disclaimer',            label: 'Disclaimer' },
  { to: '/nieuwsbrief-afmelden',  label: 'Nieuwsbrief afmelden' },
]

const accountLinks = [
  { to: '/account',              label: 'Inloggen' },
  { to: '/account/bestellingen', label: 'Mijn Bestellingen' },
  { to: '/wishlist',             label: 'Favorieten' },
  { to: '/cart',                 label: 'Winkelwagen' },
]

const paymentMethods = [
  { key: 'ideal',      label: 'iDEAL',      wide: false },
  { key: 'bancontact', label: 'Bancontact', wide: false },
  { key: 'visa',       label: 'Visa',        wide: false },
  { key: 'mastercard', label: 'Mastercard',  wide: false },
  { key: 'paypal',     label: 'PayPal',      wide: true  },
  { key: 'apple-pay',  label: 'Apple Pay',   wide: true  },
  { key: 'klarna',     label: 'Klarna',      wide: true  },
]
</script>

<style lang="scss">
@use '@styles/main' as *;

/* ── Navigation progress bar ──────────────────────────────────── */
.nav-progress-bar {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  height: 3px;
  width: 0%;
  background: var(--color-brand);
  pointer-events: none;
  opacity: 0;
  transition: none;
}

.nav-progress-bar.active {
  opacity: 1;
  width: 85%;
  transition: width 2.5s cubic-bezier(0.1, 0.05, 0.0, 1.0);
}

.nav-progress-bar.done {
  opacity: 1;
  width: 100%;
  transition: width 0.15s ease, opacity 0.3s ease 0.15s;
}

/* ── Footer fade-in ──────────────────────────────────────────── */
/* Standaard zichtbaar - JS voegt .footer-hidden toe vóór de animatie start
   zodat de footer nooit onzichtbaar blijft als JS traag is of observer faalt. */
.footer-reveal {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.6s ease, transform 0.6s var(--ease-out);
}
.footer-reveal.footer-visible {
  opacity: 1;
  transform: translateY(0);
}

/* ── Page transition ──────────────────────────────────────────── */
/* Geen mode="out-in" - oud en nieuw overlappen kort (var(--dur-fast)).
   De nieuwe pagina fadeet al IN terwijl de oude nog UIT fadeet.
   Hierdoor is er nooit een leeg zwart/wit moment. */
.page-fade-enter-active {
  transition: opacity 0.15s ease;
}
.page-fade-leave-active {
  transition: opacity 0.1s ease;
  position: absolute;
  width: 100%;
}
.page-fade-enter-from {
  opacity: 0;
}
.page-fade-leave-to {
  opacity: 1;
}

.social-icon {
  transition: background var(--dur-fast) var(--ease-out),
              color var(--dur-fast) var(--ease-out),
              transform var(--dur-fast) var(--ease-out);
}
.social-icon:hover { background: var(--color-brand); color: var(--color-ink); transform: translateY(-2px); }
.social-icon:active { transform: scale(0.95); }
@media (prefers-reduced-motion: reduce) { .social-icon { transition: none; } }
</style>
